<template>
  <asc-page-loading-indicator
    :loading="isCustomAlertsLoading && !apiPageFailure.length"
  >
    <v-container v-if="!isCustomAlertsPageActive" fluid>
      <asc-page-coming-soon
        label="Custom Analytics Page Coming Soon!"
      ></asc-page-coming-soon>
    </v-container>
    <v-container v-else fluid class="asc-custom-alerts-grid-container">
      <v-row>
        <v-col cols="12" sm="12">
          <asc-page-header>
            <template #pageTitle>
              <asc-page-title title="Custom Analytics"></asc-page-title>
            </template>

            <template #pageActions>
              <template v-if="mappedCustomAlerts.length">
                <div class="asc-custom-alerts--pageActions">
                  <asc-accent-button
                    button-text="+ Custom Analytic"
                    :on-click="handleAlertTypeDialogOpen"
                  ></asc-accent-button>
                </div>
              </template>
            </template>
          </asc-page-header>
        </v-col>
      </v-row>
      <asc-container>
        <asc-custom-alerts-grid-template
          :values="mappedCustomAlerts"
          :shared-alerts="mappedSharedAlerts"
          :hide-action-footer="hideActionFooter"
          :is-airline-user="isAirlineUser"
          :is-acmf-user="isAcmfUser"
          :user-airlines="getUserAirlines"
          :user="profile"
          :custom-alerts-full-flight-simple-permission="
            customAlertsFullFlightSimplePermission
          "
          :custom-alerts-full-flight-advanced-permission="
            customAlertsFullFlightAdvancedPermission
          "
          :custom-alerts-acmf-simple-permission="
            customAlertsAcmfSimplePermission
          "
          :custom-alerts-acmf-advanced-permission="
            customAlertsAcmfAdvancedPermission
          "
          :custom-alerts-dplf-advanced-permission="
            customAlertsDplfAdvancedPermission
          "
          @openAlertTypeDialog="handleAlertTypeDialogOpen"
          @handleEditAlert="handleEditAlert"
          @onDeleteAlert="handleDeleteAlert"
          @handleToggleAlert="handleToggleAlert"
        ></asc-custom-alerts-grid-template>
      </asc-container>
      <asc-selection-dialog
        title="Create a new custom analytic"
        subtitle="Choose the type of analytic you would like to create."
        :definition="getAlertTypes"
        :show-dialog="alertTypeDialogVisible"
        :loading="false"
        :on-close="handleAlertTypeDialogClose"
        :on-selection="handleAlertTypeSelection"
      ></asc-selection-dialog>
      <asc-flow-selection-dialog
        title="Choose a data type"
        :definition="getAdvancedAlertTypes"
        :show-dialog="dataTypeDialogVisible"
        :loading="false"
        :on-close="handleAlertTypeDialogClose"
        :on-selection="handleDataTypeSelection"
      ></asc-flow-selection-dialog>

      <asc-flow-selection-dialog
        title="CHOOSE LOGIC COMPLEXITY"
        :subtitle="getLogicComplexitySubtext"
        :custom-subtitle-class="getSubtitleClass"
        :definition="getLogicComplexityOptions"
        :show-dialog="logicComplexityDialogVisible"
        :loading="false"
        :on-back-click="handleBackClick"
        :on-close="handleLogicComplexityDialogClose"
        :on-selection="handleLogicComplexitySelection"
        :disabled="!getCustomClass"
        :custom-class="getCustomClass"
        :check-buttons="true"
        :option-icon="true"
      ></asc-flow-selection-dialog>
      <asc-custom-alert-airline-report-type-choice-dialog
        :show-dialog="airlineReportTypeDialogVisible"
        :title="artModalTitle"
        :sub-text="artModalSubText"
        :airlines="getAirlinesForAdvancedCustomAlerts"
        :report-types="getReportTypesData"
        :platforms="getPlatformsData"
        :show-airline="showAirLineSelection"
        :show-report-type="showReportTypeSelection"
        :show-platform="showPlatformSelection"
        :loading="false"
        :on-accept="handleAirlineReportTypeChoiceDialog"
        @onClose="handleAirlineReportTypeChoiceDialogClose"
        @onBackClick="handleBackClick"
      ></asc-custom-alert-airline-report-type-choice-dialog>
      <asc-dialog
        :show-dialog="showDeleteConfirmationDialog"
        :text="getDeleteConfirmationText"
        :on-primary="handleDeleteAlertConfirmation"
        :on-secondary="cancelDelete"
        :on-close="cancelDelete"
        secondary-button-text="Cancel"
        primary-button-text="Delete Analytic"
      >
        {{ getDeleteConfirmationDialogText }}
      </asc-dialog>
      <asc-confirmation
        v-if="showDeleteAlertConfirmationToast"
        :definition="deleteAlertsConfirmationToastDefinition"
        :on-close="handleCloseDeleteAlertConfirmationmToast"
      ></asc-confirmation>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import AscPageLoadingIndicator from '../../../molecules/AscPageLoadingIndicator.vue'
import AscSelectionDialog from '../../../molecules/AscSelectionDialog.vue'
import AscDialog from '../../../atoms/AscDialog.vue'
import AscConfirmation from '../../../molecules/AscConfirmation.vue'
import AscPageTitle from '../../../atoms/AscPageTitle.vue'
import AscPageHeader from '../../../atoms/AscPageHeader.vue'
import AscContainer from '../../../atoms/AscContainer.vue'
import AscAccentButton from '../../../atoms/AscAccentButton.vue'
import AscFlowSelectionDialog from '../../../molecules/AscFlowSelectionDialog.vue'
import AscCustomAlertAirlineReportTypeChoiceDialog from '../../../molecules/AscCustomAlertAirlineReportTypeChoiceDialog.vue'
import AscCustomAlertsGridTemplate from '../../../templates/AscCustomAlertsGridTemplate.vue'
import AppFeatureFlags from '@/utilities/appFeatureFlags'
import { AscCustomAlertMixin } from '@/components/mixins'
import { isValidRecAction } from '@/utilities/customAlerts'

import {
  CUSTOM_ALERTS,
  CUSTOM_ALERT_FULL_FLIGHT_DATA_ID,
  CUSTOM_ALERT_SNAPSHOT_DATA_ID,
  ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID,
  ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID,
  ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID,
  ADVANCED_CUSTOM_ALERTS,
  CUSTOM_ALERT_FULL_FLIGHT_ACTIVE_ALERT_LIMIT,
  HEALTH_MONITORING_TIER_1,
  FULL_FLIGHT_TIER_2,
  COLLINS_PHM_TIER_3,
  COLLINS_PHM_TIER_4,
  ELLIPSES_OPTION,
} from '@/utilities/appConsts'
import { mapActions, mapState, mapGetters } from 'vuex'
import {
  CUSTOM_ALERT_CONSTS as CAC,
  USER_CONSTS as UC,
  GLOBAL_FILTER_CONSTS as GF,
  ACMF_REPORT_LIST_CONSTS as ARLC,
  LOOKUP_CONSTS as LC,
} from '@/stores/consts'

export default {
  name: 'AscCustomAlertsGridContainer',

  components: {
    AscPageLoadingIndicator,
    AscCustomAlertsGridTemplate,
    AscSelectionDialog,
    AscDialog,
    AscConfirmation,
    AscContainer,
    AscPageTitle,
    AscAccentButton,
    AscPageHeader,
    AscFlowSelectionDialog,
    AscCustomAlertAirlineReportTypeChoiceDialog,
  },

  mixins: [AscCustomAlertMixin],

  data() {
    return {
      isCustomAlertsLoading: false,
      alertTypeDialogVisible: false,
      logicComplexityDialogVisible: false,
      airlineReportTypeDialogVisible: false,
      dataTypeDialogVisible: false,
      showDeleteConfirmationDialog: false,
      showDeleteAlertConfirmationToast: false,
      hideActionFooter: false,
      selectedAlertsToDelete: null,
      CUSTOM_ALERT_FULL_FLIGHT_DATA_ID,
      CUSTOM_ALERT_SNAPSHOT_DATA_ID,
      ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID,
      ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID,
      ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID,
      selectedDataType: 0,
      selectedLogicType: 0,
      airlineSelected: 0,
      reportTypeSelected: 0,
      platformSelected: 0,
      mappedUserAirlineIds: [],
      customClass: '',
      showAirLineSelection: false,
      showReportTypeSelection: false,
      showPlatformSelection: false,
      artModalTitle: '',
      artModalSubText: '',
      deleteAlertsConfirmationToastDefinition: [
        {
          mode: 'toast',
          type: 'warning',
          text: 'Analytic deleted',
          position: 'bottom',
          dismissible: true,
          hasToastAction: false,
          autoClose: true,
          icon: 'mdi-delete',
          duration: 6000,
        },
      ],
    }
  },

  computed: {
    ...mapGetters(LC.NAMESPACE, [
      'mappedLuMasterModelsAll',
      'mappedAirlineMasterModelAgreement',
    ]),
    ...mapGetters[(CAC.NAMESPACE, ['getCustomAlerts', 'getCustomAlertTypes'])],
    ...mapGetters(UC.NAMESPACE, [
      'isAirlineUser',
      'isAcmfUser',
      'mappedUserAirlines',
      'mappedUserAirlinesWithMasterModels',
      'mappedUserAirlinesWithPhmAgreements',
      'customAlertsFullFlightSimplePermission',
      'customAlertsFullFlightAdvancedPermission',
      'customAlertsAcmfSimplePermission',
      'customAlertsAcmfAdvancedPermission',
      'customAlertsDplfAdvancedPermission',
    ]),
    ...mapGetters(ARLC.NAMESPACE, [
      'mappedAcmfAirlines',
      'mappedAcmfReportTypesAll',
      'mappedAcmfAirlinesWithMasterModels',
      'mappedAcmfMasterModels',
      'mappedAcmfMasterModelsAll',
    ]),
    ...mapState(CAC.NAMESPACE, [
      'customAlerts',
      'customAlertTypes',
      'sharedAnalyticsForAirline',
    ]),
    ...mapState(GF.NAMESPACE, ['globalFilter']),
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(UC.NAMESPACE, ['profile']),

    isCustomAlertsPageActive() {
      return AppFeatureFlags.getCustomAlertsPage()
    },
    getAlertTypes() {
      let alertTypes = []

      if (this.customAlertsFullFlightSimplePermission) {
        alertTypes.push(CUSTOM_ALERTS[0])
      }

      if (this.customAlertsAcmfSimplePermission) {
        alertTypes.push(CUSTOM_ALERTS[1])
      }

      return alertTypes
    },
    isAdvancedCustomAlertsActive() {
      return AppFeatureFlags.getAdvancedCustomAlerts()
    },
    getAdvancedAlertTypes() {
      let advancedAlertTypes = []

      if (
        this.customAlertsFullFlightSimplePermission ||
        this.customAlertsFullFlightAdvancedPermission
      ) {
        advancedAlertTypes.push(ADVANCED_CUSTOM_ALERTS[0])
      }

      if (
        this.customAlertsAcmfSimplePermission ||
        this.customAlertsAcmfAdvancedPermission
      ) {
        advancedAlertTypes.push(ADVANCED_CUSTOM_ALERTS[1])
      }

      if (this.customAlertsDplfAdvancedPermission) {
        advancedAlertTypes.push(ADVANCED_CUSTOM_ALERTS[2])
      }

      return advancedAlertTypes
    },
    getLogicComplexityOptions() {
      if (this.selectedDataType === ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID)
        return [this.logicComplexityOptions()[1]]
      else if (
        this.selectedDataType === ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID &&
        this.customAlertsFullFlightSimplePermission &&
        !this.customAlertsFullFlightAdvancedPermission
      )
        return [this.logicComplexityOptions()[0]]
      else if (
        this.selectedDataType === ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID &&
        this.customAlertsFullFlightAdvancedPermission &&
        !this.customAlertsFullFlightSimplePermission
      )
        return [this.logicComplexityOptions()[1]]
      else if (
        this.selectedDataType === ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID &&
        this.customAlertsAcmfSimplePermission &&
        !this.customAlertsAcmfAdvancedPermission
      )
        return [this.logicComplexityOptions()[0]]
      else if (
        this.selectedDataType === ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID &&
        this.customAlertsAcmfAdvancedPermission &&
        !this.customAlertsAcmfSimplePermission
      )
        return [this.logicComplexityOptions()[1]]
      else return this.logicComplexityOptions()
    },
    getLogicComplexitySubtext() {
      if (this.selectedDataType === ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID)
        return 'Note: Advanced logic builder is required to build custom fault code data analytics.'
      else return ''
    },
    getSubtitleClass() {
      return this.selectedDataType === ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID
        ? 'font-italic'
        : ''
    },
    getCustomClass() {
      return this.customClass
    },
    getUserAirlines() {
      return this.mappedUserAirlines
    },

    getAirlinesForAdvancedCustomAlerts() {
      let userAirlines = this.mappedUserAirlinesWithMasterModels
      switch (this.selectedDataType) {
        case ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID:
          userAirlines = this.mappedAcmfAirlinesWithMasterModels
          break
        case ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID:
          userAirlines = this.mappedUserAirlinesWithPhmAgreements.filter(f =>
            this.mappedAirlineMasterModelAgreement.some(
              item =>
                [
                  COLLINS_PHM_TIER_4,
                  FULL_FLIGHT_TIER_2,
                  COLLINS_PHM_TIER_3,
                ].includes(item.agreementTypeId) &&
                f.airlineId == item.airlineId,
            ),
          )
          break
        case ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID:
          userAirlines = this.mappedUserAirlinesWithPhmAgreements.filter(f =>
            this.mappedAirlineMasterModelAgreement.some(
              item =>
                [
                  HEALTH_MONITORING_TIER_1,
                  COLLINS_PHM_TIER_4,
                  FULL_FLIGHT_TIER_2,
                  COLLINS_PHM_TIER_3,
                ].includes(item.agreementTypeId) &&
                f.airlineId == item.airlineId,
            ),
          )
          break
        default:
          userAirlines = this.mappedUserAirlinesWithMasterModels
      }
      return userAirlines
    },

    getReportTypesData() {
      return this.mappedAcmfReportTypesAll.map(item => {
        return {
          ...item,
          text: item.text,
          value: item.id,
        }
      })
    },
    getPlatformsData() {
      var uniquePlatforms = []
      let mappedMasterModel =
        this.isAcmfUser && !this.isAirlineUser
          ? this.mappedAcmfMasterModelsAll
          : this.mappedLuMasterModelsAll

      switch (this.selectedDataType) {
        case ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID:
          mappedMasterModel = mappedMasterModel.filter(item =>
            this.mappedAirlineMasterModelAgreement.some(
              f =>
                [
                  COLLINS_PHM_TIER_4,
                  FULL_FLIGHT_TIER_2,
                  COLLINS_PHM_TIER_3,
                ].includes(f.agreementTypeId) &&
                item.value == f.value &&
                item.airlineId == f.airlineId,
            ),
          )
          break
        case ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID:
          mappedMasterModel = mappedMasterModel.filter(item =>
            this.mappedAirlineMasterModelAgreement.some(
              f =>
                [
                  HEALTH_MONITORING_TIER_1,
                  COLLINS_PHM_TIER_4,
                  FULL_FLIGHT_TIER_2,
                  COLLINS_PHM_TIER_3,
                ].includes(f.agreementTypeId) &&
                item.value == f.value &&
                item.airlineId == f.airlineId,
            ),
          )
          break
        default:
          mappedMasterModel = [...mappedMasterModel]
      }
      mappedMasterModel
        .map(item => {
          return {
            text: item.text,
            value: item.value,
            masterModelId: item.masterModelId,
          }
        })
        .filter(function (item) {
          var i = uniquePlatforms.findIndex(x => x.value == item.value)
          if (i <= -1) {
            uniquePlatforms.push({
              text: item.text,
              value: item.value,
              masterModelId: item.masterModelId,
            })
          }
        })
      return uniquePlatforms
    },
    getFullFlightActiveCount() {
      const fullFlightActiveAlerts = this.customAlerts.filter(alertObj => {
        return (
          alertObj.isEnabled &&
          (alertObj.customAlertTypeId ===
            this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
            alertObj.customAlertTypeId ===
              this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID ||
            alertObj.customAlertTypeId ===
              this.ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID)
        )
      })
      return fullFlightActiveAlerts.length
    },

    mappedCustomAlerts() {
      return this.customAlerts.map(alertObj => {
        if (
          alertObj.customAlertTypeId ===
            this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
          alertObj.customAlertTypeId === this.CUSTOM_ALERT_SNAPSHOT_DATA_ID ||
          alertObj.customAlertTypeId ===
            this.ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID ||
          alertObj.customAlertTypeId ===
            this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID ||
          alertObj.customAlertTypeId ===
            this.ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID
        ) {
          const oneThresholdSetAndActivated =
            (alertObj.lowWarning !== null && alertObj.isLowWarningEnabled) ||
            (alertObj.lowCaution !== null && alertObj.isLowCautionEnabled) ||
            (alertObj.highCaution !== null && alertObj.isHighCautionEnabled) ||
            (alertObj.highWarning !== null && alertObj.isHighWarningEnabled)

          let configurationsSet

          if (
            alertObj.customAlertTypeId === this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID
          ) {
            configurationsSet =
              alertObj.flightPhase?.length > 0 &&
              alertObj.flightPhase !== null &&
              alertObj.aircraftIds.length > 0
          } else {
            configurationsSet = alertObj.aircraftIds.length > 0
          }

          alertObj.fullFlightActiveCount = this.getFullFlightActiveCount
          if (
            alertObj.fullFlightActiveCount >=
              CUSTOM_ALERT_FULL_FLIGHT_ACTIVE_ALERT_LIMIT &&
            (alertObj.customAlertTypeId ===
              this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
              alertObj.customAlertTypeId ===
                this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID ||
              alertObj.customAlertTypeId ===
                ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID) &&
            alertObj.isEnabled === null
          ) {
            alertObj.isEnabled = false
          }

          if (
            (!oneThresholdSetAndActivated &&
              (alertObj.customAlertTypeId ===
                this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
                alertObj.customAlertTypeId ===
                  this.CUSTOM_ALERT_SNAPSHOT_DATA_ID)) ||
            !configurationsSet
          ) {
            alertObj.isEnabled = null
          }

          alertObj.inErrorState = false

          if (
            alertObj.persistenceFrom > alertObj.persistenceTo ||
            alertObj.persistenceTo < alertObj.persistenceFrom
          ) {
            alertObj.inErrorState = true
            alertObj.isEnabled = false
          } else if (
            alertObj.aggregateDays < 0 ||
            alertObj.aggregateDays > 30 ||
            !Number.isInteger(
              alertObj.aggregateDays ? alertObj.aggregateDays : 0,
            )
          ) {
            alertObj.inErrorState = true
            alertObj.isEnabled = false
          } else if (
            alertObj.aggregateReports < 0 ||
            alertObj.aggregateReports > 60 ||
            !Number.isInteger(
              alertObj.aggregateReports ? alertObj.aggregateReports : 0,
            )
          ) {
            alertObj.inErrorState = true
            alertObj.isEnabled = false
          } else {
            if (
              (alertObj.customAlertTypeId ===
                this.ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID ||
                alertObj.customAlertTypeId ===
                  this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID) &&
              alertObj.isEnabled === null
            ) {
              alertObj.isEnabled = false
            }
            if (!this.isValidAdvancedAlert(alertObj)) {
              alertObj.inErrorState = true
              alertObj.isEnabled = false
            }
            if (
              alertObj.customAlertTypeId ===
                this.ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID &&
              alertObj.configurationType !== 'persistence' &&
              ((alertObj.aggregateDays === null &&
                alertObj.aggregateReports === null) ||
                this.isAggregateDaysAndReportsValid(
                  alertObj,
                  alertObj.configurationType,
                ))
            ) {
              alertObj.inErrorState = true
              alertObj.isEnabled = false
            }
            const isMinRangeInvalid = this.calculateMinRangeInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isMaxRangeInvalid = this.calculateMaxRangeInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isLowWarningInvalid = this.calculateLowWarningInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isLowCautionInvalid = this.calculateLowCautionInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isHighCautionInvalid = this.calculateHighCautionInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isHighWarningInvalid = this.calculateHighWarningInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            if (
              isMinRangeInvalid ||
              isMaxRangeInvalid ||
              isLowWarningInvalid ||
              isLowCautionInvalid ||
              isHighWarningInvalid ||
              isHighCautionInvalid
            ) {
              alertObj.inErrorState = true
              alertObj.isEnabled = false
            }
          }
        }

        const alertTypeObject = this.customAlertTypes.find(
          item => item.id === alertObj.customAlertTypeId,
        )

        return {
          ...alertObj,
          alertType: alertTypeObject ? alertTypeObject.displayName : '',
          ellipseOptions: ELLIPSES_OPTION.filter(
            o => o.title == 'View' || o.title == 'Delete',
          ),
        }
      })
    },
    mappedSharedAlerts() {
      return this.sharedAnalyticsForAirline.map(alertObj => {
        if (
          alertObj.customAlertTypeId ===
            this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
          alertObj.customAlertTypeId === this.CUSTOM_ALERT_SNAPSHOT_DATA_ID ||
          alertObj.customAlertTypeId ===
            this.ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID ||
          alertObj.customAlertTypeId ===
            this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID ||
          alertObj.customAlertTypeId ===
            this.ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID
        ) {
          const oneThresholdSetAndActivated =
            (alertObj.lowWarning !== null && alertObj.isLowWarningEnabled) ||
            (alertObj.lowCaution !== null && alertObj.isLowCautionEnabled) ||
            (alertObj.highCaution !== null && alertObj.isHighCautionEnabled) ||
            (alertObj.highWarning !== null && alertObj.isHighWarningEnabled)

          let configurationsSet

          if (
            alertObj.customAlertTypeId === this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID
          ) {
            configurationsSet =
              alertObj.flightPhase?.length > 0 &&
              alertObj.flightPhase !== null &&
              alertObj.aircraftIds.length > 0
          } else {
            configurationsSet = alertObj.aircraftIds.length > 0
          }

          alertObj.fullFlightActiveCount = this.getFullFlightActiveCount
          if (
            alertObj.fullFlightActiveCount >=
              CUSTOM_ALERT_FULL_FLIGHT_ACTIVE_ALERT_LIMIT &&
            (alertObj.customAlertTypeId ===
              this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
              alertObj.customAlertTypeId ===
                this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID ||
              alertObj.customAlertTypeId ===
                ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID) &&
            alertObj.isEnabled === null
          ) {
            alertObj.isEnabled = false
          }

          if (
            (!oneThresholdSetAndActivated &&
              (alertObj.customAlertTypeId ===
                this.CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
                alertObj.customAlertTypeId ===
                  this.CUSTOM_ALERT_SNAPSHOT_DATA_ID)) ||
            !configurationsSet
          ) {
            alertObj.isEnabled = null
          }

          alertObj.inErrorState = false

          if (
            alertObj.persistenceFrom > alertObj.persistenceTo ||
            alertObj.persistenceTo < alertObj.persistenceFrom
          ) {
            alertObj.inErrorState = true
            alertObj.isEnabled = false
          } else if (
            alertObj.aggregateDays < 0 ||
            alertObj.aggregateDays > 30 ||
            !Number.isInteger(
              alertObj.aggregateDays ? alertObj.aggregateDays : 0,
            )
          ) {
            alertObj.inErrorState = true
            alertObj.isEnabled = false
          } else if (
            alertObj.aggregateReports < 0 ||
            alertObj.aggregateReports > 60 ||
            !Number.isInteger(
              alertObj.aggregateReports ? alertObj.aggregateReports : 0,
            )
          ) {
            alertObj.inErrorState = true
            alertObj.isEnabled = false
          } else {
            if (
              (alertObj.customAlertTypeId ===
                this.ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID ||
                alertObj.customAlertTypeId ===
                  this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID) &&
              alertObj.isEnabled === null
            ) {
              alertObj.isEnabled = false
            }
            if (!this.isValidAdvancedAlert(alertObj)) {
              alertObj.inErrorState = true
              alertObj.isEnabled = false
            }
            if (
              alertObj.customAlertTypeId ===
                this.ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID &&
              alertObj.configurationType !== 'persistence' &&
              ((alertObj.aggregateDays === null &&
                alertObj.aggregateReports === null) ||
                this.isAggregateDaysAndReportsValid(
                  alertObj,
                  alertObj.configurationType,
                ))
            ) {
              alertObj.inErrorState = true
              alertObj.isEnabled = false
            }
            const isMinRangeInvalid = this.calculateMinRangeInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isMaxRangeInvalid = this.calculateMaxRangeInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isLowWarningInvalid = this.calculateLowWarningInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isLowCautionInvalid = this.calculateLowCautionInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isHighCautionInvalid = this.calculateHighCautionInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            const isHighWarningInvalid = this.calculateHighWarningInvalid({
              minRange: alertObj.minRange,
              maxRange: alertObj.maxRange,
              lowWarning: alertObj.lowWarning,
              lowCaution: alertObj.lowCaution,
              highCaution: alertObj.highCaution,
              highWarning: alertObj.highWarning,
            })
            if (
              isMinRangeInvalid ||
              isMaxRangeInvalid ||
              isLowWarningInvalid ||
              isLowCautionInvalid ||
              isHighWarningInvalid ||
              isHighCautionInvalid
            ) {
              alertObj.inErrorState = true
              alertObj.isEnabled = false
            }
          }
        }

        const alertTypeObject = this.customAlertTypes.find(
          item => item.id === alertObj.customAlertTypeId,
        )

        return {
          ...alertObj,
          alertType: alertTypeObject ? alertTypeObject.displayName : '',
        }
      })
    },
    getDeleteConfirmationText() {
      return 'Delete selected analytic?'
    },
    getDeleteConfirmationDialogText() {
      return 'Are you sure you want to delete the selected analytic?'
    },
  },

  watch: {
    globalFilter: {
      deep: true,
      async handler() {
        this.isCustomAlertsLoading = true
        await this.getCustomAlerts()
        this.isCustomAlertsLoading = false
      },
    },
  },

  async created() {
    this.mappedUserAirlines.forEach(x => {
      this.mappedUserAirlineIds.push(x.airlineId)
    })

    await Promise.all([
      this[ARLC.GET_ACMF_REPORT_TYPES](),
      this[ARLC.GET_ACMF_AIRLINES](),
      this[ARLC.GET_ACMF_MASTER_MODELS](),
      this[CAC.GET_CUSTOM_ALERT_TYPES](),
      this[CAC.UPDATE_ACTIVATION_NOTIFICATIONS]([]),
      this[CAC.GET_SHARED_ALERTS_FOR_AIRLINE](this.mappedUserAirlineIds),
    ])
  },

  async mounted() {
    this.isCustomAlertsLoading = true
    await this.getCustomAlerts()
    await this.getCustomAlertTypes()
    this.isCustomAlertsLoading = false
  },

  methods: {
    ...mapActions(CAC.NAMESPACE, [
      CAC.GET_CUSTOM_ALERTS,
      CAC.GET_CUSTOM_ALERT_TYPES,
      CAC.GET_SHARED_ALERTS_FOR_AIRLINE,
      CAC.DELETE_CUSTOM_ALERT,
      CAC.TOGGLE_ALERT,
      CAC.UPDATE_ACTIVATION_NOTIFICATIONS,
    ]),

    ...mapActions(ARLC.NAMESPACE, [
      ARLC.GET_ACMF_REPORT_TYPES,
      ARLC.GET_ACMF_AIRLINES,
      ARLC.GET_ACMF_MASTER_MODELS,
    ]),

    isValidAdvancedAlert(alertObj) {
      switch (alertObj.customAlertTypeId) {
        case this.ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID:
        case this.ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID:
          return (
            alertObj.ataChapters?.length > 0 &&
            isValidRecAction(alertObj.recAction) &&
            alertObj.validLogic &&
            alertObj.aircraftIds.length > 0
          )
        case this.ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID:
          return alertObj.validLogic && alertObj.aircraftIds.length > 0
        default:
          return true
      }
    },

    handleAlertTypeDialogOpen() {
      // check advanced custom alert feature flag
      if (!this.isAdvancedCustomAlertsActive) {
        // if you have both roles, open the selection dialog
        if (this.isAcmfUser && this.isAirlineUser) {
          this.alertTypeDialogVisible = true
        } else if (this.isAcmfUser) {
          // acmf user gets snapshot only
          this.$router.push({
            name: 'Custom Analytic',
            params: {
              alertType: 'acmf',
            },
          })
        } else if (this.isAirlineUser) {
          // airline user gets full-flight
          this.$router.push({
            name: 'Custom Analytic',
            params: {
              alertType: 'fullflight',
            },
          })
        }
      } else {
        // if you have airline user role, open the selection dialog
        if (this.isAirlineUser) {
          this.dataTypeDialogVisible = true
        } else if (this.isAcmfUser) {
          // acmf user gets snapshot only
          this.selectedDataType = ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID // CUSTOM_ALERT_SNAPSHOT_DATA_ID
          this.logicComplexityDialogVisible = true
        }
      }
    },
    isAggregateDaysAndReportsValid(alertObject, configurationType) {
      return (
        alertObject[configurationType] !== undefined &&
        alertObject[configurationType] !== null &&
        isNaN(alertObject[configurationType]) &&
        (alertObject[configurationType].match(/^\d+$/) === null ||
          alertObject[configurationType].match(/^\d+$/).length === 0)
      )
    },
    handleAlertTypeDialogClose() {
      this.alertTypeDialogVisible = false
      this.dataTypeDialogVisible = false
      this.showPlatformSelection = false
    },
    handleLogicComplexityDialogClose() {
      this.selectedDataType = 0
      this.selectedLogicType = 0
      this.customClass = ''
      this.logicComplexityDialogVisible = false
    },
    handleBackClick() {
      this.airlineSelected = 0
      this.reportTypeSelected = 0
      this.platformSelected = 0
      if (this.logicComplexityDialogVisible) {
        this.handleLogicComplexityDialogClose()
        if (this.isAirlineUser) this.dataTypeDialogVisible = true
      } else if (this.airlineReportTypeDialogVisible) {
        this.handleAirlineReportTypeChoiceDialogClose()
        this.showPlatformSelection = false
        this.logicComplexityDialogVisible = true
      }
    },
    handleAlertTypeSelection(value) {
      if (
        value === CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
        value.id === CUSTOM_ALERT_FULL_FLIGHT_DATA_ID ||
        value === ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID ||
        value.id === ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID
      ) {
        this.$router.push({
          name: 'Custom Analytic',
          params: {
            alertType: 'fullflight',
          },
        })
      } else {
        this.$router.push({
          name: 'Custom Analytic',
          params: {
            alertType: 'acmf',
          },
        })
      }
      this.alertTypeDialogVisible = false
    },
    handleDataTypeSelection(value) {
      this.selectedDataType = value.id
      if (value.id === ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID)
        this.handleLogicComplexitySelection(this.logicComplexityOptions()[1])
      this.logicComplexityDialogVisible = true
      this.dataTypeDialogVisible = false
    },
    handleLogicComplexitySelection(value) {
      this.selectedLogicType = value.id
      this.customClass = 'selection'
      // At Logic Complexity State
      if (
        this.logicComplexityDialogVisible &&
        !this.airlineReportTypeDialogVisible
      ) {
        this.logicComplexityDialogVisible = false
        // Determine which logic path the user chose
        if (this.selectedLogicType === 1) {
          // User chose the simple logic type
          this.handleAlertTypeSelection(this.selectedDataType)
        } else {
          // User chose the advanced logic type
          this.handleAirlineReportTypePath()
          this.airlineReportTypeDialogVisible = true
          const userAirlines = this.getAirlinesForAdvancedCustomAlerts
          const userPlatformCount = this.getUserPlatformCount()
          const userplatforms = this.getPlatformsData
          if (
            this.selectedDataType !== 3 &&
            userAirlines.length === 1 &&
            userPlatformCount === 1
          ) {
            this.airlineReportTypeDialogVisible = false
            const data = {
              airlineSelect: userAirlines[0].airlineId,
              airlineText: userAirlines[0].airlineName,
              platformSelect: userplatforms[0].masterModelId,
              platformText: userplatforms[0].text,
              reportText: null,
              reportType: null,
            }
            this.handleAirlineReportTypeChoiceDialog(data)
          }
        }
      } else if (this.airlineReportTypeDialogVisible) {
        // TODO: Final Destination after selectiing airline and/or report type
      }
    },

    handleAirlineReportTypePath() {
      // Path 1: If User is an Airline User AND an ACMF User AND Chose ACMF Data Type
      // Sub Path: If user has only 1 airline
      // Path 2: If User is an Airline User OR an ACMF User AND Chose Full Flight OR DPLF Data Type
      switch (this.selectedDataType) {
        case 3: // ACMF Data ; Path 1
          if (this.isAcmfUser) {
            const userAirlines = this.getAirlinesForAdvancedCustomAlerts
            const userPlatformCount = this.getUserPlatformCount()
            userPlatformCount === 1
              ? (this.showPlatformSelection = false)
              : (this.showPlatformSelection = true)
            if (userAirlines.length === 1) {
              this.showAirLineSelection = false
              this.showReportTypeSelection = true
              this.artModalTitle =
                userPlatformCount === 1 && userAirlines.length === 1
                  ? 'Choose a Report Type'
                  : 'Choose a Platform and Report Type'
            } else {
              this.showAirLineSelection = true
              this.showReportTypeSelection = true
              this.artModalTitle =
                userPlatformCount === 1 && userAirlines.length > 1
                  ? 'Choose an Airline and Report Type'
                  : 'Choose an Airline, Platform, and Report Type'
            }
          } else {
            this.artModalTitle = 'Error'
            this.artModalSubText =
              'Error. Please contact your administrator for the correct roles. For ACMF, please have the ACMF User Role. For Full Flight or DPLF, please have the Airline User Role.'
          }
          break
        case 4: // Full Flight Data Path 2
        case 5: // DPLF Data
          if (this.isAirlineUser) {
            const userAirlines = this.getAirlinesForAdvancedCustomAlerts
            const userPlatformCount = this.getUserPlatformCount()
            userPlatformCount === 1
              ? (this.showPlatformSelection = false)
              : (this.showPlatformSelection = true)
            if (userAirlines.length === 1) {
              this.showAirLineSelection = false
              this.showReportTypeSelection = false
              this.artModalTitle = 'Choose a Platform'
            } else {
              this.showAirLineSelection = true
              this.showReportTypeSelection = false
              this.artModalTitle =
                userPlatformCount === 1
                  ? 'Choose an Airline'
                  : 'Choose an Airline and Platform'
            }
          } else {
            this.artModalTitle = 'Error'
            this.artModalSubText =
              'Error. Please contact your administrator for the correct roles. For ACMF, please have the ACMF User Role. For Full Flight or DPLF, please have the Airline User Role.'
          }
          break
      }
      this.airlineReportTypeDialogVisible = true
    },

    handleAirlineReportTypeChoiceDialog(data) {
      this.airlineSelected = data.airlineSelect
      this.reportTypeSelected = data.reportType
      this.platformSelected = data.platformSelect
      const customAlertType = this.getAdvancedAlertId()
      let generateAlertType
      // Temporary condition to push router only for acmf
      if (customAlertType.name === 'ACMF Advanced')
        generateAlertType = 'advanced-acmf'
      else if (customAlertType.name === 'CPL Advanced')
        generateAlertType = 'advanced-fullflight'
      else if (customAlertType.name === 'DPLF Advanced')
        generateAlertType = 'advanced-dplf'
      else return

      const customAlertData = {
        airline: data.airlineText,
        reportType: data.reportText,
        customAlertTypeId: customAlertType.id,
        airlineId: this.airlineSelected,
        reportNumberId: this.reportTypeSelected,
        platform: data.platformText,
        platformId: this.platformSelected,
      }

      this.$router.push({
        name: 'Custom Analytic',
        params: {
          alertType: generateAlertType,
          buildData: customAlertData,
        },
      })
      this.selectedLogicType = 0
      this.airlineReportTypeDialogVisible = false
    },

    getAdvancedAlertId() {
      let dataTypeName =
        this.selectedDataType === ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID
          ? 'DPLF'
          : this.selectedDataType === ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID // CUSTOM_ALERT_SNAPSHOT_DATA_ID
          ? 'ACMF'
          : 'CPL'
      dataTypeName = dataTypeName + ' Advanced'
      const actualAlert = this.customAlertTypes.filter(
        alert => alert.name === dataTypeName,
      )
      return actualAlert ? actualAlert[0] : {}
    },
    handleAirlineReportTypeChoiceDialogClose() {
      this.airlineReportTypeDialogVisible = false
    },

    handleEditAlert(whichAlert) {
      let assignedAlertType
      let assignedAlertTypeID

      switch (whichAlert.alertType) {
        case CUSTOM_ALERT_SNAPSHOT_DATA_ID:
          assignedAlertType = 'acmf'
          assignedAlertTypeID = CUSTOM_ALERT_SNAPSHOT_DATA_ID
          break
        case CUSTOM_ALERT_FULL_FLIGHT_DATA_ID:
          assignedAlertType = 'fullflight'
          assignedAlertTypeID = CUSTOM_ALERT_FULL_FLIGHT_DATA_ID
          break
        case ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID:
          assignedAlertType = 'advanced-acmf'
          assignedAlertTypeID = ADVANCED_CUSTOM_ALERTS_SNAPSHOT_DATA_ID
          break
        case ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID:
          assignedAlertType = 'advanced-fullflight'
          assignedAlertTypeID = ADVANCED_CUSTOM_ALERTS_FULL_FLIGHT_DATA_ID
          break
        case ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID:
          assignedAlertType = 'advanced-dplf'
          assignedAlertTypeID = ADVANCED_CUSTOM_ALERTS_DPLF_DATA_ID
          break
      }

      this.$router.push({
        name: 'Custom Analytic',
        params: {
          alertType: assignedAlertType,
          alertTypeID: assignedAlertTypeID,
          alertId: whichAlert.id,
        },
      })
    },
    handleCloseDeleteAlertConfirmationmToast() {
      this.showDeleteAlertConfirmationToast = false
    },
    async handleDeleteAlertConfirmation() {
      this.showDeleteConfirmationDialog = false
      this.isCustomAlertsLoading = true
      await this[CAC.DELETE_CUSTOM_ALERT](
        this.selectedAlertsToDelete.baseAlertId,
      )
      this.showDeleteAlertConfirmationToast = true
      await Promise.all([
        this.getCustomAlerts(),
        this[CAC.GET_SHARED_ALERTS_FOR_AIRLINE](this.mappedUserAirlineIds),
      ])
      this.isCustomAlertsLoading = false
      this.selectedAlertsToDelete = null
    },

    handleDeleteAlert(alertsToDelete) {
      this.selectedAlertsToDelete = alertsToDelete
      this.showDeleteConfirmationDialog = true
    },

    cancelDelete() {
      this.showDeleteConfirmationDialog = false
      this.showDeleteAlertConfirmationToast = false
    },
    async handleToggleAlert(value) {
      this.isCustomAlertsLoading = true
      await this[CAC.TOGGLE_ALERT](value)
      this.isCustomAlertsLoading = false
    },
    getUserPlatformCount() {
      const userPlatforms = this.getPlatformsData
      var platforms = []
      for (var i = 0; i < userPlatforms.length; i++) {
        var value = userPlatforms[i].value
        if (platforms.indexOf(value) === -1) {
          platforms.push(value)
        }
      }
      return platforms.length
    },
  },
}
</script>

<style lang="scss">
.asc-custom-alerts-grid-container {
  background-color: $gray-100;

  .asc-custom-alerts--pageActions {
    display: flex;
    justify-content: flex-end;
  }

  .container {
    padding: 16px;
  }
}
</style>
